import React, {useRef, useState} from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Typography, Tooltip, IconButton } from '@material-ui/core';
import NetworkZoneList from './NetworkZoneList';
import { hasPermission, createHistory } from '../../sc-utilities';
import { FindInPage } from '@material-ui/icons';

const styles = theme => ({
    "@global": {
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: 'none',
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='priorityLow']": {
            color: theme.palette.warning.light
        },
        "[class*='priorityNormal']": {
            color: theme.palette.success.light
        },
        "[class*='priorityHigh']": {
            color: theme.palette.error.light
        },
        "[class*='policyAllow']": {
            color: theme.palette.success.dark
        },
        "[class*='policyDeny']": {
            color: theme.palette.error.dark
        },
        "[class*='userActive']": {
            color: theme.palette.success.dark,
            marginTop: 8.5,
            fontSize: 15
        },
        "[class*='userInactive']": {
            color: theme.palette.error.dark,
            padding: 1,
            margin: 1,
            fontSize: 15
        }
    },
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    },
    policyAllow: {
        color: theme.palette.success.dark,
    },
    policyDeny: {
        color: theme.palette.error.dark,
    },
    priorityLow: {
        color: theme.palette.warning.light
    },
    priorityNormal: {
        color: theme.palette.success.light
    },
    priorityHigh: {
        color: theme.palette.error.light
    }
});

const NetworkZone = props => {
    const tableRef = useRef();
    const { columns, classes, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, networkZoneID: null, redirectPath: '' });

    let visible = columns && columns['NetworkZone'];

    const tableColumns = [
        {
            title: translations["action"] || "Action", field: 'Action',
            hidden: visible ? visible['Action'] : false,
            filtering: false,
            render: rowData => <>
                {
                    hasPermission(permissions, 'network_zone_load') ?
                        <Tooltip title={translations["network_zone_details"] || "Network zone details"}>
                            <IconButton
                                id={`network_zone_details_${rowData.NetworkZoneID}`}
                                component='div'
                                onClick={() => redirectHandler(`/network-zone/edit?NetworkZoneID=${rowData.NetworkZoneID}`)}
                            >
                                <FindInPage />
                            </IconButton>
                        </Tooltip> : null
                }
            </>
        },
        {
            title: translations["name"] || 'Name', field: 'Name',
            hidden: visible ? visible['Name'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["subnet"] || 'Subnet', field: 'Subnet',
            hidden: visible ? visible['Subnet'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.Subnet ? rowData.Subnet : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["priority"] || 'Priority', field: 'Priority',
            hidden: visible ? visible['Priority'] : false,
            lookup: { 'low': translations['priority_low'] || 'Low', 'normal': translations['priority_normal'] || 'Normal', 'high': translations['priority_high'] || 'High' },
            defaultFilter: ['low', 'normal', 'high'],
            filtering: true,
            render: rowData => {
                return <>
                    <CustomChip
                        className={rowData.Priority === 'low' ? "priorityLow" : rowData.Priority === 'normal' ? "priorityNormal" : "priorityHigh"}
                        key={'priority'}
                        value={rowData.Priority === 'low' ? translations['priority_low'] || "Low" : rowData.Priority === 'normal' ? translations['priority_normal'] || "Normal" : translations['priority_high'] || "High"}
                    />
                </>
            }

        },
        {
            title: translations["policy"] || 'Policy', field: 'Policy',
            hidden: visible ? visible['Policy'] : false,
            lookup: { 'deny': translations['policy_deny'] || 'Deny', 'allow': translations['policy_allow'] || 'Allow' },
            defaultFilter: ['deny', 'allow'],
            filtering: true,
            render: rowData => {
                return <>
                    <CustomChip
                        className={rowData.Policy === 'allow' ? "policyAllow" : "policyDeny"}
                        key={'policy'}
                        value={rowData.Policy === 'allow' ? translations['policy_allow'] || "Allow" : translations['policy_deny'] || "Deny"}
                    />
                </>
            }
        },
        {
            title: translations["default_language"] || 'Default language', field: 'DefaultLanguage',
            hidden: visible ? visible['DefaultLanguage'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.DefaultLanguage ? rowData.DefaultLanguage : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        },
        {
            title: translations["default_keyboard_layout"] || 'Default keyboard layout', field: 'DefaultKeyboardLayout',
            hidden: visible ? visible['DefaultKeyboardLayout'] : false,
            filtering: true,
            render: rowData => {
                return <Typography variant={'caption'}>{rowData.DefaultKeyboardLayout ? rowData.DefaultKeyboardLayout : translations['short_not_assigned'] || 'n/a'}</Typography>;
            }
        }
    ];

    const redirectHandler = (path, networkZoneID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, networkZoneID: networkZoneID, redirectPath: path });
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    const CustomChip = props => {
        const { keyValue, value, className } = props;
        return (
            <Typography key={keyValue} className={className}>{value}</Typography>
        );
    };

    return (
        <div className={classes.root}>
            {
                hasPermission(permissions, 'network_zone_list') ?
                    <NetworkZoneList
                        tableColumns={tableColumns}
                        redirectHandler={redirectHandler}
                        tableRef={tableRef}
                    /> : null
            }
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
}

export default connect(
    mapStateToProps,
    null
)(withStyles(styles, { withTheme: true })(NetworkZone));
